import { useRef, useCallback, useEffect } from 'react';

const toPath = (file) =>
  file.startsWith('/') ? `${process.env.PUBLIC_URL}${file}` : `${process.env.PUBLIC_URL}/sounds/${file}`;

// Shared sound-effect player used by the game components. Replaces the
// ad-hoc `new Audio(...).play()` calls that were duplicated across
// MemoryGame, SoundMatching, WordMatching, AlphabetOrdering, QuickQuiz and
// PicturePuzzle.
export default function useAudio() {
  const activeRef = useRef(null);
  const cacheRef = useRef({});

  useEffect(() => {
    return () => {
      if (activeRef.current) {
        activeRef.current.pause();
      }
    };
  }, []);

  // Optionally pre-create Audio objects for a batch of files so playback
  // starts instantly and load errors surface up front (used by MemoryGame).
  const preload = useCallback((files) => {
    cacheRef.current = {};
    files.filter(Boolean).forEach((file) => {
      const path = toPath(file);
      const audio = new Audio(path);
      audio.onerror = () => console.warn(`Sound file not found: ${path}`);
      cacheRef.current[path] = audio;
    });
  }, []);

  // interrupt: true (default) stops whatever this hook is currently
  // playing before starting the new sound. Pass false for effects that
  // should be allowed to overlap (matches each component's prior behavior).
  const play = useCallback((file, { interrupt = true } = {}) => {
    if (!file) return;
    const path = toPath(file);
    const cached = cacheRef.current[path];

    if (interrupt && activeRef.current && !activeRef.current.paused) {
      activeRef.current.pause();
      activeRef.current.currentTime = 0;
    }

    const audio = cached || new Audio(path);
    audio.currentTime = 0;
    audio.play().catch(() => {});
    activeRef.current = audio;
  }, []);

  const stop = useCallback(() => {
    if (activeRef.current) {
      activeRef.current.pause();
      activeRef.current.currentTime = 0;
    }
  }, []);

  return { play, preload, stop };
}
